import { AppError } from '@app/errors/AppError'

import { snapshotFromJwt } from '@app/utils/jwt'

import type { AuthSessionSnapshot } from '@app/packages/api/AuthSessionManager'

import { configSelector } from '@app/store/slices/config'
import { createThunk } from '@app/store/thunk'

import { getApplicationBridge } from './contexts/applicationBridge'

/**
 * The app answers `session_token` from 3.46.0. Older ones never reply, so asking them only buys
 * a five-second timeout before the same teardown — see `requestExternalToken` in `run.client`.
 */
export const APP_SESSION_TOKEN_PROTOCOL = 4

export const canAskAppForSessionToken = (protocol: number, isApp: boolean) => isApp && protocol >= APP_SESSION_TOKEN_PROTOCOL

/**
 * Ask the app for an access token that is valid now. The app runs its own `ensureAccessToken`
 * first, which is the point: the page asks precisely when its own copy has aged out and it has no
 * refresh material of its own.
 *
 * `null` means nobody is signed in — an answer, not a failure. Anything else throws, and the
 * caller holds the session rather than ending it on the strength of not knowing.
 */
export const requestAppSessionToken = () =>
  createThunk(async (dispatch, getState): Promise<AuthSessionSnapshot | null> => {
    const { isApp, appProtocol } = configSelector(getState())
    if (!canAskAppForSessionToken(appProtocol, isApp)) throw new AppError('session_token: the app cannot answer this')

    // Absent until `run.client` has built it — asking before then is a bug, not a lost session.
    const bridge = dispatch(getApplicationBridge())
    if (!bridge) throw new AppError('session_token: the application bridge is not ready')

    const { access_token } = await bridge.sendAction('session_token', {})
    if (!access_token) return null

    const snapshot = snapshotFromJwt(access_token)
    if (!snapshot) throw new AppError('session_token: access token is not a usable JWT')
    return snapshot
  })
